/**
 * Отправка контрольного результата.
 *
 * Клиент проверяет очевидные условия до отправки, чтобы не тратить лимит
 * запросов и не показывать пользователю отказ там, где он предсказуем.
 * Окончательное решение всё равно принимает сервер (ТЗ FR-VT-01).
 */

import type { SyncId, TypingMetrics } from '@type2x/shared-contracts';
import { meetsVerifiedRequirements } from '@type2x/learning-engine';

import type {
  ControlResultPayload,
  ControlTask,
  ControlTestGateway,
  ControlVerdict,
  IntegritySignalsPayload,
} from '../contracts/control-test.js';

/**
 * Почему результат не засчитан.
 *
 * `below-requirements` решается на клиенте до отправки; остальные три приходят
 * от шлюза и различают ответ сервера, отсутствие аккаунта и обрыв связи.
 */
export type SubmissionRefusal = 'below-requirements' | 'needs-account' | 'offline' | 'not-accepted';

/** Итог попытки отправить контрольный результат. */
export type SubmissionOutcome =
  | { readonly sent: true; readonly verdict: ControlVerdict }
  | { readonly sent: false; readonly reason: SubmissionRefusal };

/** Собирает результат контрольного теста для отправки. */
export function buildControlPayload(
  task: ControlTask,
  syncId: SyncId,
  metrics: TypingMetrics,
  signals: IntegritySignalsPayload,
): ControlResultPayload {
  return {
    syncId,
    language: task.language,
    layoutId: task.layoutId,
    wpm: metrics.wpm,
    accuracy: metrics.accuracy,
    durationMs: metrics.elapsedMs,
    characters: metrics.typedCharacters,
    unseenText: true,
    signals,
  };
}

/**
 * Отправляет результат, если он может быть засчитан.
 *
 * Недостижение минимальных требований — не отказ сервера, а предсказуемый
 * исход: пользователю сообщается сразу.
 */
export async function submitControlResult(
  gateway: ControlTestGateway,
  payload: ControlResultPayload,
): Promise<SubmissionOutcome> {
  if (!meetsVerifiedRequirements(payload.accuracy, payload.durationMs, payload.characters)) {
    return { sent: false, reason: 'below-requirements' };
  }

  const submission = await gateway.submit(payload);
  if (submission.kind !== 'verdict') return { sent: false, reason: submission.kind };

  return { sent: true, verdict: submission.verdict };
}
